'use client';

import { Spinner } from '@heroui/react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { APP_ROUTES } from '@/shared/constants/app-routes';
import { buildAppRoute } from '@/shared/functions/build-app-route';
import { generateVideoThumbnail } from '@/shared/functions/generate-video-thumbnail';
import { defineMediaPreviewType } from '@/shared/functions/get-media-type';
import type { Media } from '@/shared/functions/tanstack-query/get/medias/get-blob';
import useGetMediaBlob from '@/shared/functions/tanstack-query/get/medias/get-blob/use-index';
export type MediaPreviewType = 'pdf' | 'video' | 'image' | 'spreadsheet';
export interface MediaPreviewProps {
  media: Media;
  show_video_cover?: boolean;
  should_redirect?: boolean;
  className?: string;
}
export default function MediaPreview({
  media,
  should_redirect = !!media.redirectUrl,
  show_video_cover = false,
  className
}: MediaPreviewProps) {
  const {
    data: blob,
    isLoading: isBlobLoading
  } = useGetMediaBlob({
    media_id: media?.id
  });
  const mediaType = defineMediaPreviewType(media?.mimetype);
  const [src, setSrc] = useState<string>('/assets/images/not_found.jpg');
  useEffect(() => {
    if (!blob) return;
    let isMounted = true;
    const url = URL.createObjectURL(blob);
    const process = async () => {
      let finalSrc = url;
      if (mediaType === 'video' && show_video_cover) {
        finalSrc = await generateVideoThumbnail({
          url
        });
      }
      if (isMounted) setSrc(finalSrc);
    };
    process();
    return () => {
      isMounted = false;
      URL.revokeObjectURL(url);
    };
  }, [blob, mediaType, show_video_cover]);
  if (isBlobLoading) return <div className="flex bg-[#e8e8e8] size-full items-center justify-center">
				<Spinner color="default" className="mx-auto" />
			</div>;
  if (mediaType === 'video' && !show_video_cover) return <video autoPlay loop muted playsInline src={src} className={cn('object-cover size-full', className)}>
				<track kind="captions" />
				<p>
					O seu navegador não tem suporte a vídeo HTML. Em vez disso, aqui está
					<Link as={'div'} href={src} download={src}>
						o link do vídeo
					</Link>
					.
				</p>
			</video>;
  if (mediaType === 'video' || mediaType === 'image') {
    if (should_redirect) {
      return <Link href={media.redirectUrl || buildAppRoute(APP_ROUTES.PUBLIC.MEDIA, {
        media_id: media.id
      })} rel="noreferrer noopener" target="_blank">
					<Image src={src} quality={100} priority unoptimized={media?.mimetype.includes('gif')} alt={media?.name || 'image is not provided'} title={media?.name || 'image is not provided'} fill sizes="50vw" className={cn('object-cover', className)} crossOrigin="use-credentials" />
				</Link>;
    }
    return <Image src={src} quality={100} priority unoptimized={media?.mimetype.includes('gif')} alt={media?.name || 'image is not provided'} title={media?.name || 'image is not provided'} fill sizes="50vw" className={cn('object-cover', className)} crossOrigin="use-credentials" />;
  }
  if (mediaType === 'spreadsheet') {
    return <h1>Preview indisponível</h1>;
  }
  return <div className={cn('size-full overflow-hidden', className)}>
			<iframe src={src} title="pdf preview" allowFullScreen className={'size-full border-none'} />
		</div>;
}